import { ApiClient, ApiResponse } from '../client';
import { User, AuthCredentials, RegisterData, AuthResponse } from '../models';

export interface DropShippingEmailSettings {
  recipientEmailsEnabledByDefault: boolean;
  brandName: string;
  logoUrl: string;
  logoKey?: string;
  headerImageUrl: string;
  headerImageKey?: string;
  primaryColor: string;
  accentColor: string;
  replyToEmail: string;
  footerText: string;
}

/**
 * Servicio para gestionar la autenticación de usuarios
 */
export class AuthService {
  private client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Iniciar sesión con email y contraseña
   * @param credentials Credenciales de usuario
   */
  public async login(credentials: AuthCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await this.client.post<AuthResponse>('/auth/login', credentials);

    // Si la autenticación es exitosa, guardar el token
    if (response.success && response.data) {
      this.client.setAuthToken(response.data.token);

      // Guardar token en localStorage para mantener la sesión
      localStorage.setItem('auth_token', response.data.token);

      // También guardar los datos del usuario
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response;
  }

  /**
   * Cerrar sesión del usuario
   */
  public async logout(): Promise<ApiResponse<null>> {
    // Llamar al endpoint de logout
    const response = await this.client.get<null>('/auth/logout');

    // Limpiar token y datos de usuario sin importar la respuesta
    this.client.setAuthToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');

    return response;
  }

  /**
   * Registrar un nuevo usuario
   * @param userData Datos para registrar al usuario
   */
  public async register(userData: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await this.client.post<AuthResponse>('/auth/register', userData);

    // Si el registro es exitoso, guardar el token
    if (response.success && response.data) {
      this.client.setAuthToken(response.data.token);

      // Guardar token en localStorage para mantener la sesión
      localStorage.setItem('auth_token', response.data.token);

      // También guardar los datos del usuario
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response;
  }

  /**
   * Obtener información del usuario actual
   */
  public async getProfile(): Promise<ApiResponse<User>> {
    return await this.client.get<User>('/auth/me');
  }

  /**
   * Actualizar información del perfil de usuario
   */
  public async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    return await this.client.put<User>('/auth/updatedetails', userData);
  }

  public async getDropShippingEmailSettings(): Promise<ApiResponse<DropShippingEmailSettings>> {
    return await this.client.get<DropShippingEmailSettings>('/auth/drop-shipping-email-settings');
  }

  public async updateDropShippingEmailSettings(
    settings: DropShippingEmailSettings
  ): Promise<ApiResponse<DropShippingEmailSettings>> {
    return await this.client.put<DropShippingEmailSettings>('/auth/drop-shipping-email-settings', settings);
  }

  public async uploadDropShippingEmailAsset(
    assetType: 'logo' | 'header',
    file: File
  ): Promise<ApiResponse<{ assetType: string; url: string; key: string; settings: DropShippingEmailSettings }>> {
    const formData = new FormData();
    formData.append('image', file);

    return await this.client.post(`/auth/drop-shipping-email-assets/${assetType}`, formData);
  }

  /**
   * Actualizar contraseña del usuario
   */
  public async updatePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse<AuthResponse>> {
    return await this.client.put<AuthResponse>('/auth/updatepassword', passwordData);
  }

  /**
   * Solicitar restablecimiento de contraseña
   */
  public async forgotPassword(email: string): Promise<ApiResponse<{ resetToken: string }>> {
    return await this.client.post<{ resetToken: string }>('/auth/forgotpassword', { email });
  }

  /**
   * Validar token de restablecimiento de contraseña
   */
  public async validateResetToken(resetToken: string): Promise<ApiResponse<{ valid: boolean; email: string }>> {
    return await this.client.get<{ valid: boolean; email: string }>(`/auth/validate-reset-token/${resetToken}`);
  }

  /**
   * Restablecer contraseña con token
   */
  public async resetPassword(
    resetToken: string,
    password: string
  ): Promise<ApiResponse<AuthResponse>> {
    return await this.client.put<AuthResponse>(
      `/auth/resetpassword/${resetToken}`,
      { password }
    );
  }

  /**
   * Verificar si el usuario está autenticado y cargar datos de sesión
   * @returns true si hay un usuario autenticado
   */
  public restoreSession(): boolean {
    const token = localStorage.getItem('auth_token');

    if (token) {
      // Establecer el token en el cliente para futuras solicitudes
      this.client.setAuthToken(token);

      // Verificar proactivamente si la sesión es válida mediante un ping
      this.pingSession().catch(error => {
        console.error('Error al verificar sesión durante restauración:', error);
        // No invalidamos la sesión automáticamente en caso de error de red
        // Será manejado por AuthContext
      });

      return true;
    }

    return false;
  }

  /**
   * Verificar silenciosamente si la sesión es válida
   * Este método es utilizado internamente y no debe exponer errores al usuario
   */
  private async pingSession(): Promise<boolean> {
    try {
      const response = await this.client.get('/auth/ping', {
        useCache: false, // No cachear esta verificación
        timeout: 5000 // Timeout más corto para no bloquear la UI
      });

      return response.success;
    } catch (error) {
      console.error('Error en ping de sesión:', error);
      return false;
    }
  }

  /**
   * Obtener el usuario guardado en localStorage
   * @returns Datos del usuario o null si no hay sesión
   */
  public getStoredUser(): User | null {
    const userData = localStorage.getItem('user');

    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (e) {
        return null;
      }
    }

    return null;
  }
} 
